import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Form, Modal, Space, Table } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, SectionCard, type Tone } from '../../theme/listkit'
import { renderAvatarName } from '../../theme/columns'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupsAdminErrorMessage } from './adminErrors'
import { MemberSelect } from '../notification_rules/MemberSelect'
import { useGroupsAudience, type GroupsAudienceState } from '../groups_settings/useGroupsAudience'

interface MemberRow {
  user_id: string
  role: 'organizer' | 'co_organizer' | 'member'
  status: 'active' | 'pending' | 'left' | 'removed' | 'banned'
  is_founding: boolean
  joined_at: string | null
  name: string
  avatar_url: string | null
  class_year: string | null
  /** profiles.role — used only to keep the Tester tag off admins (they're never "parked"). */
  profile_role: string | null
}

/**
 * Maps a failed "Add founding members" insert to a plain-English error. A
 * `not_in_groups_audience` refusal (the group_members_audience_guard trigger,
 * 20261018120300_groups_audience_side_effects.sql) names the OFFENDING
 * member, unlike the generic groupsAdminErrorMessage() mapping — the trigger
 * itself only raises a machine key, so this looks up which of the candidates
 * the admin just picked is neither an admin nor a Groups tester (the guard's
 * exact rule) and names that one. Falls through to the generic mapping for
 * every other error, and when (unexpectedly) every candidate IS in the
 * audience — the insert should then have succeeded.
 *
 * SF-5 (fix round 2): the guard's rule is "admin OR ACTIVE tester" —
 * `_groups_tester_active` requires rollout mode `testers`/`everyone`, so a
 * candidate on the `groups_testers` list is PARKED (not active) while the
 * state is `admins` or `off`. The round-1 lookup skipped every candidate in
 * `testerIds` when searching for the offender, so a refused PARKED tester
 * fell through to the generic "add them as a tester first" toast — advice
 * the admin had already followed. This now finds that candidate too, in a
 * SECOND pass (after the "not a tester at all" case, which still takes
 * priority — that toast's advice is actually actionable), and names them
 * with the correct, state-specific explanation instead.
 */
export function addFoundingMembersErrorMessage(
  error: unknown,
  candidates: { user_id: string; name: string; role: string | null }[],
  testerIds: ReadonlySet<string>,
  audienceState: GroupsAudienceState | null,
): string {
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error)
  if (message === 'not_in_groups_audience') {
    const offender = candidates.find((c) => c.role !== 'admin' && !testerIds.has(c.user_id))
    if (offender) {
      return `${offender.name} isn't an admin or a Groups tester. Add them as a tester first (Group Settings).`
    }
    const parkedTester = candidates.find((c) => c.role !== 'admin' && testerIds.has(c.user_id))
    if (parkedTester) {
      const stateLabel = audienceState === 'off' ? 'Off' : 'Admins only'
      return `${parkedTester.name} is a Groups tester, but testers are parked while Groups is ${stateLabel}.`
    }
  }
  return groupsAdminErrorMessage(error)
}

const roleLabel = (role: string): string =>
  role === 'organizer' ? 'Organizer' : role === 'co_organizer' ? 'Co-organizer' : 'Member'

const roleTone = (role: string): Tone => (role === 'organizer' ? 'gold' : role === 'co_organizer' ? 'jaguar' : 'neutral')

const statusTone = (status: string): Tone => {
  if (status === 'active') return 'green'
  if (status === 'pending') return 'gold'
  return 'neutral' // left, removed, banned
}

/**
 * Members table on the Group show page.
 *
 * Two queries — group_members then profiles by user_id — the SAME shape the
 * mobile app's useGroupMembers hook uses (mobile/src/features/groups/useGroupMembers.ts),
 * because a shared FK from group_members to profiles doesn't exist (it points at
 * auth.users). We read `profiles` here (not `directory_profiles`, which the
 * mobile roster uses): the admin console must be able to see and manage EVERY
 * member, including ones who opted out of the public directory — filtering them
 * out would silently hide roster entries an admin needs to moderate.
 */
export function GroupMembersTable({ groupId, groupKind }: { groupId: string; groupKind: 'interest' | 'class_chapter' }) {
  const { message, modal } = App.useApp()
  const audience = useGroupsAudience()
  const [rows, setRows] = useState<MemberRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busyUser, setBusyUser] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [addIds, setAddIds] = useState<string[]>([])
  const [adding, setAdding] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: members, error: mErr } = await supabaseClient
        .from('group_members')
        .select('user_id, role, status, is_founding, joined_at')
        .eq('group_id', groupId)
        .in('status', ['active', 'pending'])
      if (mErr) throw mErr
      const ids = (members ?? []).map((m) => m.user_id)
      if (ids.length === 0) {
        setRows([])
        return
      }
      const { data: profiles, error: pErr } = await supabaseClient
        .from('profiles')
        .select('user_id, name, avatar_url, class_year, role')
        .in('user_id', ids)
      if (pErr) throw pErr
      const byId = new Map((profiles ?? []).map((p) => [p.user_id, p]))
      const merged: MemberRow[] = (members ?? []).map((m) => {
        const p = byId.get(m.user_id)
        return {
          user_id: m.user_id,
          role: m.role,
          status: m.status,
          profile_role: p?.role ?? null,
          is_founding: m.is_founding,
          joined_at: m.joined_at,
          name: p?.name ?? 'Unknown member',
          avatar_url: p?.avatar_url ?? null,
          class_year: p?.class_year ?? null,
        }
      })
      const roleRank: Record<string, number> = { organizer: 0, co_organizer: 1, member: 2 }
      merged.sort((a, b) => roleRank[a.role] - roleRank[b.role] || a.name.localeCompare(b.name))
      setRows(merged)
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [groupId, message])

  useEffect(() => {
    void load()
  }, [load])

  async function setRole(userId: string, role: 'organizer' | 'co_organizer' | 'member') {
    setBusyUser(userId)
    try {
      const { error } = await supabaseClient.rpc('set_member_role', { p_group: groupId, p_user: userId, p_role: role })
      if (error) throw error
      message.success('Role updated')
      await load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusyUser(null)
    }
  }

  async function removeMember(userId: string, ban: boolean) {
    setBusyUser(userId)
    try {
      const { error } = await supabaseClient.rpc('remove_member', { p_group: groupId, p_user: userId, p_ban: ban })
      if (error) throw error
      message.success(ban ? 'Member banned' : 'Member removed')
      await load()
    } catch (e) {
      message.error(groupsAdminErrorMessage(e))
    } finally {
      setBusyUser(null)
    }
  }

  function confirmRemove(row: MemberRow, ban: boolean) {
    modal.confirm({
      title: ban ? `Ban ${row.name}?` : `Remove ${row.name}?`,
      content: ban
        ? 'They will be removed from the group and unable to rejoin.'
        : 'They will be removed from the group. They can rejoin if the group allows it.',
      okText: ban ? 'Ban' : 'Remove',
      okButtonProps: { danger: true },
      onOk: () => removeMember(row.user_id, ban),
    })
  }

  async function addFoundingMembers() {
    if (addIds.length === 0) return
    setAdding(true)
    try {
      const now = new Date().toISOString()
      const { error } = await supabaseClient.from('group_members').insert(
        addIds.map((user_id) => ({
          group_id: groupId,
          user_id,
          status: 'active' as const,
          role: 'member' as const,
          joined_via: 'admin',
          is_founding: true,
          joined_at: now,
        })),
      )
      if (error) throw error
      message.success(`Added ${addIds.length} founding member${addIds.length === 1 ? '' : 's'}`)
      setAddOpen(false)
      setAddIds([])
      await load()
    } catch (e) {
      // group_members_audience_guard (20261018120300) refuses a non-admin,
      // non-tester addIds row with just the machine key `not_in_groups_audience`
      // — look the candidates up by name so the toast can say WHICH one.
      const { data: candidates } = await supabaseClient.from('profiles').select('user_id, name, role').in('user_id', addIds)
      message.error(
        addFoundingMembersErrorMessage(
          e,
          (candidates ?? []) as { user_id: string; name: string; role: string | null }[],
          audience.testerIds,
          audience.state,
        ),
      )
    } finally {
      setAdding(false)
    }
  }

  return (
    <SectionCard
      title="Members"
      sub={`${rows.length} active or pending`}
      action={groupKind === 'interest' ? 'Add founding members' : undefined}
      onAction={groupKind === 'interest' ? () => setAddOpen(true) : undefined}
    >
      <Table<MemberRow>
        dataSource={rows}
        rowKey="user_id"
        loading={loading}
        pagination={rows.length > 20 ? { pageSize: 20 } : false}
      >
        <Table.Column<MemberRow> dataIndex="name" title="Member" render={(_: unknown, r) => renderAvatarName(r.name)} />
        <Table.Column<MemberRow>
          dataIndex="role"
          title="Role"
          render={(value: string, r) => (
            <Space size={4}>
              <Pill tone={roleTone(value)}>{roleLabel(value)}</Pill>
              {r.is_founding && <Pill tone="gold">Founding member</Pill>}
              {/* Non-admin active members are the "Groups testers" audience while it isn't
                  Everyone yet (spec §2d) — every other member/tester distinction is invisible
                  to the admin without this, since a tester's row looks just like a member's. */}
              {r.status === 'active' &&
                r.profile_role !== 'admin' &&
                audience.state !== null &&
                audience.state !== 'everyone' &&
                audience.testerIds.has(r.user_id) && <Pill tone="jaguar">Tester</Pill>}
            </Space>
          )}
        />
        <Table.Column<MemberRow>
          dataIndex="status"
          title="Status"
          render={(value: string) => <Pill tone={statusTone(value)}>{value}</Pill>}
        />
        <Table.Column<MemberRow>
          dataIndex="joined_at"
          title="Joined"
          render={(value: string | null) => (value ? new Date(value).toLocaleDateString() : '—')}
        />
        <Table.Column<MemberRow>
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, r) => (
            <Space wrap>
              {r.role !== 'organizer' && (
                <Button size="small" loading={busyUser === r.user_id} onClick={() => setRole(r.user_id, 'organizer')}>
                  Make organizer
                </Button>
              )}
              {r.role !== 'co_organizer' && (
                <Button size="small" loading={busyUser === r.user_id} onClick={() => setRole(r.user_id, 'co_organizer')}>
                  Make co-organizer
                </Button>
              )}
              {r.role !== 'member' && (
                <Button size="small" loading={busyUser === r.user_id} onClick={() => setRole(r.user_id, 'member')}>
                  Make member
                </Button>
              )}
              <Button size="small" danger loading={busyUser === r.user_id} onClick={() => confirmRemove(r, false)}>
                Remove
              </Button>
              <Button size="small" danger loading={busyUser === r.user_id} onClick={() => confirmRemove(r, true)}>
                Ban
              </Button>
            </Space>
          )}
        />
      </Table>

      <Modal
        title="Add founding members"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false)
          setAddIds([])
        }}
        onOk={addFoundingMembers}
        okText="Add"
        confirmLoading={adding}
        okButtonProps={{ disabled: addIds.length === 0 }}
      >
        <Form layout="vertical">
          <Form.Item
            label="Members"
            help="They join immediately with the “Founding member” tag, the same as anyone who joins in a group’s first 30 days."
          >
            <MemberSelect value={addIds} onChange={setAddIds} />
          </Form.Item>
          {/*
           * SF-5 (fix round 2): while the state is 'admins' or 'off',
           * testers on the list are PARKED (`_groups_tester_active` requires
           * mode 'testers'/'everyone') — the old note said "only admins and
           * Groups testers can be added" in every non-Everyone state, which
           * was false there: NO non-admin, tester or not, can be added.
           * 'testers' mode keeps the original copy — it's still accurate.
           */}
          {(audience.state === 'admins' || audience.state === 'off') && (
            <div data-testid="add-founding-audience-note" style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
              While Groups is {audience.state === 'off' ? 'Off' : 'Admins only'}, only admins can be added (testers are parked).
            </div>
          )}
          {audience.state === 'testers' && (
            <div data-testid="add-founding-audience-note" style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub }}>
              While Groups isn't open to Everyone, only admins and Groups testers can be added.
            </div>
          )}
        </Form>
      </Modal>
      <div style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginTop: 8 }}>
        Only active and pending members are shown.
      </div>
    </SectionCard>
  )
}
